import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { RED, RED_LIGHT, GREEN, BORDER, MUTED } from "assets/theme/brandColors";

/**
 * One recipe as a clickable result row.
 *
 * Lifted verbatim out of the schedule builder's swap panel, which is where the
 * shape was worked out: title, then a single muted meta line of "·"-joined
 * facts. Browse and swap show the same object and now render it from the same
 * component, so the two can't drift into two different-looking recipe cards.
 *
 * `meta` is a plain array; falsy entries are dropped, so callers pass
 * `time && \`${time} min\`` and don't have to pre-filter.
 *
 * `badge` is the single primary badge (meal type, on browse and the swap
 * panel). `extraBadges` renders alongside it for a fact that needs its own
 * visual identity rather than folding into `meta` — e.g. the "needs advance
 * prep" chip (utils/passiveWait.js), which would read as just more muted text
 * next to the active-time stat if it lived in `meta` instead.
 *
 * Colours come from brandColors only. The palette pairs used here:
 *   #222 on #fff (16:1), MUTED on #fff (5.3:1), RED on RED_LIGHT (7.6:1) —
 * all clear of the 4.5:1 body-text bar the palette is built around.
 */
function RecipeResultCard({
  title,
  meta,
  description,
  badge,
  extraBadges,
  selected,
  selectable,
  onClick,
  onToggleSelect,
}) {
  const metaLine = (meta || []).filter(Boolean).join(" · ");

  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1.5px solid",
        borderColor: selected ? RED : BORDER,
        bgcolor: selected ? RED_LIGHT : "#fff",
        borderRadius: "10px",
        p: 1.5,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        transition: "border-color 0.12s, background 0.12s",
        "&:hover": onClick ? { borderColor: RED, bgcolor: RED_LIGHT } : {},
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexWrap: "wrap",
            mb: 0.4,
          }}
        >
          <Box
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: "#222",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            {title}
          </Box>
          {badge && (
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: badge.color,
                bgcolor: badge.bg,
                px: 0.75,
                py: "2px",
                borderRadius: "5px",
                fontFamily: "Roboto, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              {badge.label}
            </Box>
          )}
          {/* Sentence-shaped chips (an emoji + short phrase, e.g. the
              "needs advance prep" badge) — no uppercase transform, unlike
              `badge` above, since these aren't short category words. */}
          {(extraBadges || []).filter(Boolean).map((b, i) => (
            <Box
              key={`${b.label}-${i}`}
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                color: b.color,
                bgcolor: b.bg,
                px: 0.75,
                py: "2px",
                borderRadius: "5px",
                fontFamily: "Roboto, sans-serif",
              }}
            >
              {b.label}
            </Box>
          ))}
        </Box>

        {metaLine && (
          <Box sx={{ fontSize: 11.5, color: MUTED, fontFamily: "Roboto, sans-serif" }}>
            {metaLine}
          </Box>
        )}

        {description && (
          <Box
            sx={{
              fontSize: 12,
              color: MUTED,
              fontFamily: "Roboto, sans-serif",
              mt: 0.6,
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {description}
          </Box>
        )}
      </Box>

      {selectable && (
        <Box
          component="button"
          aria-label={selected ? `Remove ${title} from picks` : `Add ${title} to picks`}
          onClick={(e) => {
            // The card body opens the recipe; only this control selects it.
            e.stopPropagation();
            onToggleSelect();
          }}
          sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: "7px",
            border: "1.5px solid",
            borderColor: selected ? GREEN : BORDER,
            bgcolor: selected ? GREEN : "#fff",
            color: "#fff",
            fontSize: 14,
            lineHeight: 1,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Roboto, sans-serif",
            "&:hover": { borderColor: selected ? GREEN : RED },
          }}
        >
          {selected ? "✓" : ""}
        </Box>
      )}
    </Box>
  );
}

RecipeResultCard.propTypes = {
  title: PropTypes.string.isRequired,
  meta: PropTypes.arrayOf(PropTypes.node),
  description: PropTypes.string,
  badge: PropTypes.shape({
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    bg: PropTypes.string.isRequired,
  }),
  extraBadges: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      bg: PropTypes.string.isRequired,
    })
  ),
  selected: PropTypes.bool,
  selectable: PropTypes.bool,
  onClick: PropTypes.func,
  onToggleSelect: PropTypes.func,
};

export default RecipeResultCard;
